import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  assignSubscriptionToOrganization,
} from '../../slice/organizationsSlice';
import { fetchAllSubscriptions } from '../../slice/subscriptionSlice';
import { useToast } from '../../components/Toast';
import ADD from './assets/add.svg';
import DeleteModal from '../../components/atomic/DeleteModal';
import { BASE_URL } from '../../components/constants';

const OrganizationModal = ({ isOpen, onClose, onConfirm, initial = {} }) => {
  const [form, setForm] = useState({
    organization_name: '',
    organization_description: '',
    primary_color: '#FFD700',
    logo_blob_path: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [tempOrganizationId, setTempOrganizationId] = useState(null);

  useEffect(() => {
    if (initial && initial.organization_id) {
      setForm({
        organization_name: initial.organization_name || '',
        organization_description: initial.organization_description || '',
        primary_color: initial.primary_color || '#FFD700',
        logo_blob_path: initial.logo_blob_path || '',
      });
      setTempOrganizationId(null); // Use existing organization ID for updates
    } else {
      setForm({ organization_name: '', organization_description: '', primary_color: '#FFD700', logo_blob_path: '' });
      // Generate a temporary UUID for new organizations
      setTempOrganizationId(crypto.randomUUID());
    }
    setLogoFile(null);
  }, [initial, isOpen]);

  if (!isOpen) return null;

  const handleLogoUpload = async () => {
    if (!logoFile) return form.logo_blob_path;
    
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      // Use temp organization ID for new organizations, or existing ID for updates
      const orgId = initial.organization_id || tempOrganizationId;
      formData.append('organization_id', orgId);
      
      const response = await fetch(`${BASE_URL}/organizations/upload`, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.blob_path;
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
    } finally {
      setIsUploading(false);
    }
    return form.logo_blob_path;
  };

  const handleSubmit = async () => {
    if (!form.organization_name.trim()) return;
    
    const logoPath = await handleLogoUpload();
    // For new organizations, include the temp organization ID so it can be used for creation
    const submitData = { ...form, logo_blob_path: logoPath };
    if (tempOrganizationId) {
      submitData.organization_id = tempOrganizationId;
    }
    onConfirm(submitData);
  };

  const handleClose = async () => {
    // Clean up any uploaded logo if the modal is closed without saving
    if (logoFile && tempOrganizationId && !initial.organization_id) {
      try {
        // Call cleanup endpoint to remove orphaned logo file
        await fetch(`${BASE_URL}/organizations/cleanup-logo/${tempOrganizationId}`, {
          method: 'DELETE',
        });
        console.log('Cleaned up temp logo for organization:', tempOrganizationId);
      } catch (error) {
        console.error('Failed to cleanup temp logo:', error);
      }
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h2 className="text-xl font-semibold mb-4">
          {initial.organization_id ? 'Organisation bearbeiten' : 'Neue Organisation erstellen'}
        </h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Organisationsname *</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={form.organization_name}
            onChange={e => setForm({ ...form, organization_name: e.target.value })}
            placeholder="Organisationsname eingeben"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung (optional)</label>
          <textarea
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={form.organization_description}
            onChange={e => setForm({ ...form, organization_description: e.target.value })}
            placeholder="Beschreibung eingeben"
            rows="3"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Primärfarbe</label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
              value={form.primary_color}
              onChange={e => setForm({ ...form, primary_color: e.target.value })}
            />
            <input
              type="text"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
              value={form.primary_color}
              onChange={e => setForm({ ...form, primary_color: e.target.value })}
              placeholder="#FFD700"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Diese Farbe wird für die Organisation verwendet</p>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
          <div className="flex items-center space-x-3">
            {form.logo_blob_path && initial.organization_id && (
              <img 
                src={`${BASE_URL}/organizations/media/${initial.organization_id}`}
                alt="Current logo"
                className="w-12 h-12 object-contain border border-gray-300 rounded"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files[0])}
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Logo wird für die Organisation verwendet</p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            onClick={handleClose}
          >
            Abbrechen
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={isUploading}
          >
            {isUploading ? 'Wird hochgeladen...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Organization = () => {
  const dispatch = useDispatch();
  const { organizations, loading } = useSelector(state => state.organizations);
  const { subscriptions } = useSelector(state => state.subscriptions);
  const [modalOpen, setModalOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [deleteOrg, setDeleteOrg] = useState(null);
  const [assignSubscriptionModal, setAssignSubscriptionModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const { addToast, ToastContainer } = useToast();

  useEffect(() => {
    dispatch(fetchAllOrganizations());
    dispatch(fetchAllSubscriptions());
  }, [dispatch]);

  const handleAdd = () => {
    setEditOrg(null);
    setModalOpen(true);
  };

  const handleEdit = org => {
    setEditOrg(org);
    setModalOpen(true);
  };

  const handleDelete = org => {
    setDeleteOrg(org);
  };

  const handleModalConfirm = async (form) => {
    try {
      if (editOrg) {
        await dispatch(updateOrganization({ ...form, organization_id: editOrg.organization_id })).unwrap();
        addToast('Organisation erfolgreich aktualisiert', 'success');
      } else {
        await dispatch(createOrganization(form)).unwrap();
        addToast('Organisation erfolgreich erstellt', 'success');
      }
      setModalOpen(false);
      dispatch(fetchAllOrganizations());
    } catch (err) {
      // Clean up the uploaded logo if organization creation failed
      if (form.organization_id && !editOrg) {
        try {
          await fetch(`${BASE_URL}/organizations/cleanup-logo/${form.organization_id}`, {
            method: 'DELETE',
          });
          console.log('Cleaned up logo after failed organization creation:', form.organization_id);
        } catch (cleanupError) {
          console.error('Failed to cleanup logo after failed creation:', cleanupError);
        }
      }
      addToast('Fehler beim Speichern', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteOrganization(deleteOrg.organization_id)).unwrap();
      addToast('Organisation gelöscht', 'success');
      setDeleteOrg(null);
      dispatch(fetchAllOrganizations());
    } catch (err) {
      addToast('Fehler beim Löschen', 'error');
    }
  };

  const handleAssignSubscription = (org) => {
    setSelectedOrg(org);
    setAssignSubscriptionModal(true);
  };

  const handleSubscriptionAssign = async (subscription_id) => {
    try {
      await dispatch(assignSubscriptionToOrganization({
        organization_id: selectedOrg.organization_id,
        subscription_id: subscription_id
      })).unwrap();
      addToast('Abonnement erfolgreich zugewiesen', 'success');
      setAssignSubscriptionModal(false);
      setSelectedOrg(null);
      dispatch(fetchAllOrganizations());
    } catch (err) {
      addToast('Fehler beim Zuweisen des Abonnements', 'error');
    }
  };

  return (
    <div className="mb-16 pt-5 mt-8 mx-auto px-4 sm:px-6 rounded lg:px-8">
      <ToastContainer />
      <div className="pl-32 pr-40">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold mb-6">Organisationen verwalten</h1>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center font-bold rounded-lg px-4 py-2 bg-custom-yellow text-white hover:bg-yellow-500 transition-colors shadow-sm"
          >
            <img src={ADD} alt="add" />
            <span className="ml-2">Neue Organisation</span>
          </button>
        </div>
        <div className="overflow-y-auto max-h-[60vh] no-scrollbar">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farbe</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {organizations.map(org => (
                <tr key={org.organization_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {org.logo_blob_path ? (
                      <img 
                        src={`${BASE_URL}/organizations/media/${org.organization_id}`}
                        alt="Organization logo"
                        className="w-8 h-8 object-contain"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-xs text-gray-500">No</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{org.organization_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{org.organization_description}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-block w-6 h-6 rounded-full border border-gray-300 align-middle mr-2" style={{ backgroundColor: org.primary_color }} />
                    <span className="text-xs text-gray-500">{org.primary_color}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <button
                      onClick={() => handleEdit(org)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >Bearbeiten</button>
                    <button
                      onClick={() => handleDelete(org)}
                      className="text-red-600 hover:text-red-900"
                    >Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <OrganizationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
        initial={editOrg || {}}
      />
      <DeleteModal
        isOpen={!!deleteOrg}
        onClose={() => setDeleteOrg(null)}
        onDelete={handleDeleteConfirm}
        title="Organisation löschen"
        message={`Möchten Sie die Organisation "${deleteOrg?.organization_name}" wirklich löschen?`}
      />
      
      {/* Subscription Assignment Modal */}
      {assignSubscriptionModal && selectedOrg && (
        <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-semibold mb-4">
              Abonnement für "{selectedOrg.organization_name}" zuweisen
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Abonnement auswählen</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
                onChange={(e) => {
                  if (e.target.value === 'none') {
                    handleSubscriptionAssign(null);
                  } else {
                    handleSubscriptionAssign(e.target.value);
                  }
                }}
                defaultValue=""
              >
                <option value="" disabled>Abonnement auswählen...</option>
                <option value="none">Kein Abonnement</option>
                {subscriptions.map(sub => (
                  <option key={sub.subscription_id} value={sub.subscription_id}>
                    {sub.subscription_name} - {sub.max_tasks} Aufgaben, {sub.max_pages} Seiten
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                onClick={() => {
                  setAssignSubscriptionModal(false);
                  setSelectedOrg(null);
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organization;